import React from 'react';
import { useIntl } from 'react-intl';

import { useCurrentTheme } from 'app/shared/theme';
import {
  PropertyMap,
  Seating,
  Tag,
  VenueForFanFacingEvent,
} from 'app/shared/typings';
import { Typography } from 'app/shared/components/atoms';
import { AccordionSectionProps } from 'app/fan/components/molecules';
import { ReactComponent as CommercialBuilding } from 'icons/building.svg';
import { ReactComponent as Residential } from 'icons/residential.svg';

import {
  useAccessibilityMappings,
  useAdditionalInformationMapping,
  useAgeRestrictionMappings,
  useFoodAndBeverageMappings,
  useOtherMappings,
  usePetMappings,
  useSeatingTypeMappings,
  useVenueTypeMappings,
} from './mappings';

export interface DynamicSectionsProps {
  isCompetition: boolean;
  isVenueConfirmed: boolean;
  venue?: VenueForFanFacingEvent;
  seating?: Seating;
  propertyMap?: PropertyMap;
  isFilmed: boolean;
}

function filterByVisibleToFans(tag: Tag) {
  return !!tag.isVisibleToFans;
}

export const useDynamicSections = ({
  isCompetition,
  isVenueConfirmed,
  venue,
  seating,
  propertyMap,
  isFilmed,
}: DynamicSectionsProps) => {
  const { formatMessage } = useIntl();
  const { colors } = useCurrentTheme();

  const dynamicSections: AccordionSectionProps[] = [];
  const venueTypeMappings: Record<string, AccordionSectionProps> =
    useVenueTypeMappings({ formatMessage });
  const seatingTypeMappings: Record<string, AccordionSectionProps> =
    useSeatingTypeMappings({ formatMessage });
  const foodAndBeverageMappings: Record<string, AccordionSectionProps> =
    useFoodAndBeverageMappings({ formatMessage });
  const ageRestrictionMappings: Record<string, AccordionSectionProps> =
    useAgeRestrictionMappings({ formatMessage });
  const petMappings: Record<string, AccordionSectionProps> = usePetMappings({
    formatMessage,
  });
  const getAccessibilityMappings = useAccessibilityMappings({ formatMessage });
  const otherMappings: Record<string, AccordionSectionProps> = useOtherMappings(
    { formatMessage }
  );
  const getAdditionalInformationSection = useAdditionalInformationMapping({
    formatMessage,
  });

  // Age
  if (propertyMap?.over25Only) {
    dynamicSections.push(ageRestrictionMappings.over25Only);
  } else if (propertyMap?.over23Only) {
    dynamicSections.push(ageRestrictionMappings.over23Only);
  } else if (propertyMap?.over21Only) {
    dynamicSections.push(ageRestrictionMappings.over21Only);
  } else if (propertyMap?.over19Only) {
    dynamicSections.push(ageRestrictionMappings.over19Only);
  } else if (propertyMap?.over18Only) {
    dynamicSections.push(ageRestrictionMappings.over18Only);
  }

  // Beverage
  if (propertyMap?.alcoholNotPermittedOrSold) {
    dynamicSections.push(foodAndBeverageMappings.alcoholNotPermittedOrSold);
  } else if (
    propertyMap?.alcoholForSale &&
    propertyMap?.byob &&
    propertyMap?.noRedWine
  ) {
    dynamicSections.push(
      foodAndBeverageMappings.alcoholForSaleAndByobNoRedWine
    );
  } else if (propertyMap?.alcoholForSale && propertyMap?.noRedWine) {
    dynamicSections.push(
      foodAndBeverageMappings.alcoholForSaleNoRedWineForSale
    );
  } else if (propertyMap?.alcoholForSale && propertyMap?.byob) {
    dynamicSections.push(foodAndBeverageMappings.alcoholForSaleAndByob);
  } else if (propertyMap?.alcoholForSale) {
    dynamicSections.push(foodAndBeverageMappings.alcoholForSaleNoByob);
  } else if (propertyMap?.byob && propertyMap?.noRedWine) {
    dynamicSections.push(foodAndBeverageMappings.byobNoRedWine);
  } else if (propertyMap?.byob) {
    dynamicSections.push(foodAndBeverageMappings.byob);
  }

  // Food
  if (propertyMap?.foodAvailableForSale && propertyMap?.noOutsideFood) {
    dynamicSections.push(foodAndBeverageMappings.foodForSaleNoOutsideFood);
  } else if (propertyMap?.foodAvailableForSale) {
    dynamicSections.push(foodAndBeverageMappings.foodAvailableForSale);
  } else if (propertyMap?.noOutsideFood) {
    dynamicSections.push(foodAndBeverageMappings.noOutsideFood);
  } else {
    dynamicSections.push(foodAndBeverageMappings.snacksAllowed);
  }

  // Seating
  if (isVenueConfirmed) {
    const seatingType = seating?.key || 'unknown';
    dynamicSections.push(seatingTypeMappings[seatingType]);
  }

  // Afternoon
  if (propertyMap?.afternoonShow) {
    dynamicSections.push(otherMappings.afternoonShow);
  }

  // Outdoors or indoors
  if (propertyMap?.outdoors) {
    dynamicSections.push(otherMappings.outdoors);
  } else {
    dynamicSections.push(otherMappings.indoors);
  }

  // First come, first served
  if (isCompetition) {
    dynamicSections.push(otherMappings.firstComeFirstServed);
  }

  // Venue Category or Type
  if (isVenueConfirmed && venue) {
    const { venueCategories } = venue;
    const visibleCategories: Tag[] | undefined = venueCategories.filter(
      filterByVisibleToFans
    );

    if (visibleCategories?.length > 0) {
      const displayCategory = visibleCategories[0];

      const details = (
        <Typography color={colors.primary.white.base}>
          {formatMessage({
            id: 'event.eventCharacteristicsList.venueCategory',
          })}
        </Typography>
      );

      const icon = propertyMap?.residential ? Residential : CommercialBuilding;

      dynamicSections.push({
        title:
          displayCategory.name.charAt(0) +
          displayCategory.name.slice(1).toLowerCase(),
        icon,
        details,
      });
    } else if (propertyMap?.commercial) {
      dynamicSections.push(venueTypeMappings.commercial);
    } else if (propertyMap?.residential) {
      dynamicSections.push(venueTypeMappings.residential);
    }
  }

  // Filmed
  if (isFilmed) {
    dynamicSections.push(otherMappings?.filmedShow);
  }

  // Accessibility
  const accessibilityMappings = getAccessibilityMappings({
    propertyMap,
  });
  if (propertyMap?.narrowDoorwaysNoStairs) {
    dynamicSections.push(accessibilityMappings.narrowDoorwaysNoStairs);
  } else if (propertyMap?.notWheelchairAccessible) {
    dynamicSections.push(accessibilityMappings.notWheelchairAccessible);
  } else if (propertyMap?.wheelchairAccessibleNoStairs) {
    dynamicSections.push(accessibilityMappings.wheelchairAccessibleNoStairs);
  } else if (propertyMap?.wheelchairAccessibleWithStairs) {
    dynamicSections.push(accessibilityMappings.wheelchairAccessibleWithStairs);
  } else {
    dynamicSections.push(accessibilityMappings.default);
  }

  // Pets
  if (propertyMap?.dog && propertyMap?.cat) {
    dynamicSections.push(petMappings.dogAndCat);
  } else if (propertyMap?.dog) {
    dynamicSections.push(petMappings.dog);
  } else if (propertyMap?.cat) {
    dynamicSections.push(petMappings.cat);
  }

  // Additional Info (Smoking, 420, & Parking)
  const additionalInformation = getAdditionalInformationSection({
    propertyMap,
  });
  if (additionalInformation) {
    dynamicSections.push(additionalInformation);
  }

  return dynamicSections;
};
